import moment from 'moment-timezone';
import { Moment } from 'moment';
import { DATETIME_FORMAT, TIME_FORMAT, TIME_FORMAT_WITH_SECONDS } from './constants';
import { DayOfWeek } from '../types/DayOfWeek';

export const DAYS_OF_WEEK: DayOfWeek[] = [
  {
    fullLabel: 'Sunday',
    label: 'Sun',
    value: 1,
    isWeekday: false,
    shortLabel: 'S',
    defaultSelected: false,
  },
  {
    fullLabel: 'Monday',
    label: 'Mon',
    value: 2,
    isWeekday: true,
    shortLabel: 'M',
    defaultSelected: true,
  },
  {
    fullLabel: 'Tuesday',
    label: 'Tue',
    value: 3,
    isWeekday: true,
    shortLabel: 'T',
    defaultSelected: true,
  },
  {
    fullLabel: 'Wednesday',
    label: 'Wed',
    value: 4,
    isWeekday: true,
    shortLabel: 'W',
    defaultSelected: true,
  },
  {
    fullLabel: 'Thursday',
    label: 'Thu',
    value: 5,
    isWeekday: true,
    shortLabel: 'R',
    defaultSelected: true,
  },
  {
    fullLabel: 'Friday',
    label: 'Fri',
    value: 6,
    isWeekday: true,
    shortLabel: 'F',
    defaultSelected: true,
  },
  {
    fullLabel: 'Saturday',
    label: 'Sat',
    value: 7,
    isWeekday: false,
    shortLabel: 'S',
    defaultSelected: false,
  },
];

/**
 * Put a UTC date into a certain timezone
 * @param timestamp '2021-01-01 00:00:00'
 * @param timezone 'America/New_York'
 */
export const utcToTimezone = (timestamp: string, timezone: string): Moment => (
  moment.utc(timestamp)
    .tz(timezone));

export const timezoneToUtc = (timestamp: string, timezone: string): Moment => (
  moment.tz(timestamp, timezone)
    .utc());

export const getUtcDate = (date: string, timezone: string): string => (
  timezoneToUtc(date, timezone).format(DATETIME_FORMAT));

export const getUtcTodStart = (
  startDateTime: string, todStart: string, timezone: string,
): string => (
  timezoneToUtc(`${startDateTime.split(' ')[0]} ${todStart}`, timezone)
    .format(TIME_FORMAT_WITH_SECONDS));

export const getUtcTodEnd = (
  startDateTime: string, todEnd: string, timezone: string,
): string => (
  timezoneToUtc(`${startDateTime.split(' ')[0]} ${todEnd}`, timezone)
    .add(-1, 'seconds')
    .format(TIME_FORMAT_WITH_SECONDS));

// export const timestampToXVal = (timestamp: Moment) =>
// timestamp.hours() + (timestamp.minutes() / 60) + (timestamp.seconds() / 3600);

export const decimalToTime = (decimal: number): string => {
  let decimalTime = decimal;
  decimalTime = decimalTime * 60 * 60;
  const hours = Math.floor((decimalTime / (60 * 60)));
  decimalTime -= (hours * 60 * 60);
  const minutes = Math.floor((decimalTime / 60));
  decimalTime -= (minutes * 60);
  let seconds = Math.round(decimalTime);
  seconds = (seconds === 60) ? 0 : seconds;

  return [hours, minutes, seconds].map((t) => (`${t}`.padStart(2, '0')))
    .join(':');
};

export const generateListOfTimes = (interval = 15): string[] => {
  const times: string[] = []; // time array
  let tt = 0; // start time

  // loop to increment the time and push results in array
  for (let i = 0; tt < 24 * 60; i++) {
    const hh = Math.floor(tt / 60); // getting hours of day in 0-24 format
    const mm = (tt % 60); // getting minutes of the hour in 0-55 format
    times.push(`${`${hh}`.padStart(2, '0')}:${`${mm}`.padStart(2, '0')}`);
    // times[i] = ("0" + (hh % 12)).slice(-2) + ':' + ("0" + mm).slice(-2) +
    // ap[Math.floor(hh / 12)]; // pushing data in array in [00:00 - 12:00 AM/PM format]
    tt += interval;
  }
  return times;
};

/**
 * Turn a time string into decimal hours
 * @param time the time string
 */
export const timeAsDecimal = (time: string): number => {
  const parsedTime = moment(time, TIME_FORMAT);
  return parsedTime.hours() + (parsedTime.minutes() / 60) + (parsedTime.seconds() / 60 / 60);
};

export const getDaysOfWeekString = (selectedDaysOfWeek: DayOfWeek[]): string => {
  const numWeekdaysSelected = selectedDaysOfWeek.filter((d) => d.isWeekday).length;
  const numWeekendsSelected = selectedDaysOfWeek.filter((d) => !d.isWeekday).length;

  const allWeekdaysChecked = numWeekdaysSelected === 5;
  const someWeekdaysChecked = numWeekdaysSelected < 5 && numWeekdaysSelected > 0;

  const allWeekendsChecked = numWeekendsSelected === 2;
  const someWeekendsChecked = numWeekendsSelected < 2 && numWeekendsSelected > 0;

  if (allWeekendsChecked && allWeekdaysChecked) {
    return 'Weekdays and Weekends';
  }
  if (allWeekdaysChecked && !allWeekendsChecked && !someWeekendsChecked) {
    return 'Weekdays';
  }
  if (allWeekendsChecked && !allWeekdaysChecked && !someWeekdaysChecked) {
    return 'Weekends';
  }
  return selectedDaysOfWeek
    .sort((a, b) => a.value - b.value)
    .map((d) => d.shortLabel)
    .join('');
};
